/**
 * A Flow session that expires while the page is open (the authtool library's RedirectToLogin only checks at load).
 *
 * index.jsx wraps the global fetch with `watchSessionExpiry`: a 401 from Flow's own APIs (same origin: /api/, /sandbox/,
 * /bff/api/...) asks authtool-bff once whether the session is still there (`GET /bff/session`). Only when that says no is
 * the expiry announced (`onSessionExpired`); SessionExpiredNotice then tells the person and sends them to sign in, back to
 * the same page. A 401 of one API while the session is fine (a missing token for one service, a preview link)
 * announces nothing. Every response is handed back untouched; once the expiry is announced, Flow's own API calls stop
 * going out (polling pauses: they answer 401 at once) until the person signs in again, which reloads the page.
 *
 * Two more ways the expiry is noticed (a page that makes no API call would otherwise keep showing stale data):
 * - `watchSessionOnFocus`: the tab coming back after more than FOCUS_PROBE_MS asks /bff/session the same way;
 * - `watchPreviewFrames`: the api-gateway's "Sign in to see this preview" page, inside Flow's preview frame, posts
 *   {type: 'flow:preview-unauthorized'} to Flow (flow-helm-chart files/lua/token-exchange.lua); from Flow's own
 *   origin only, that announces the expiry at once (the gateway already found the session refused).
 *   The same listener takes {type: 'flow:preview-login-loop', project, service}: the gateway stopped a previewed app
 *   that keeps redirecting to a sign-in under its own preview address; `onPreviewLoginLoop` listeners
 *   (PreviewLoginLoopNotice) show a banner with a link to the service's code. Not an expiry.
 */
const EXPIRED_EVENT = 'flow:session-expired';
const REDIRECT_AT_KEY = 'flow.sessionExpiredRedirectAt';
// Paths whose 401 says nothing about Flow's session (the sign-in flow itself).
const IGNORED = /^\/bff\/(?:login|logout|session|oauth|oidc)/;

export const PREVIEW_UNAUTHORIZED = 'flow:preview-unauthorized';
export const PREVIEW_LOGIN_LOOP = 'flow:preview-login-loop';
const LOGIN_LOOP_EVENT = 'flow:preview-login-loop-seen';
const NAME = /^[\w.-]{1,128}$/;
export const FOCUS_PROBE_MS = 5 * 60 * 1000;

let checking = null;
let expired = false;
let lastProbeAt = Date.now();

function pathOf(input) {
  const raw = typeof input === 'string' ? input : (input?.url || (input instanceof URL ? input.href : ''));
  try {
    const url = new URL(raw, window.location.origin);
    return url.origin === window.location.origin ? url.pathname : null;
  } catch {
    return null;
  }
}

/** Whether a 401 for this request may mean the session is gone: a same-origin API call, not the sign-in flow. */
export function watchesPath(input) {
  const path = pathOf(input);
  return Boolean(path) && !IGNORED.test(path) && /^\/(?:api|sandbox|bff)\//.test(path);
}

/** The sign-in URL that comes back to this page. */
export function loginUrl(href = window.location.href) {
  return `/bff/login?redirect_uri=${encodeURIComponent(href)}`;
}

/** Whether Flow sent the person to sign in less than a minute ago (then a click, not another automatic redirect). */
export function redirectedRecently(now = Date.now()) {
  try {
    const at = Number(window.sessionStorage.getItem(REDIRECT_AT_KEY));
    return Number.isFinite(at) && now - at < 60000;
  } catch {
    return false;
  }
}

/** Goes to sign in, noting the time so a session that cannot be restored never loops. */
export function goToLogin() {
  try { window.sessionStorage.setItem(REDIRECT_AT_KEY, String(Date.now())); } catch { /* storage blocked: still go */ }
  window.location.assign(loginUrl());
}

/** Calls `listener()` once the session is found expired; returns the unsubscribe. */
export function onSessionExpired(listener) {
  const handle = () => listener();
  window.addEventListener(EXPIRED_EVENT, handle);
  if (expired) { listener(); }
  return () => window.removeEventListener(EXPIRED_EVENT, handle);
}

/** Whether the expiry was announced (then Flow's own API calls are held until the person signs in again). */
export function isSessionExpired() { return expired; }

function announce() {
  if (expired) { return; }
  expired = true;
  window.dispatchEvent(new CustomEvent(EXPIRED_EVENT));
}

// What /bff/session answers: 401/403, or a session whose valid_until is already past, is gone.
async function sessionGone(response) {
  if (response.status === 401 || response.status === 403) { return true; }
  if (!response.ok) { return false; } // the bff restarting: not an expiry
  try {
    const body = await response.json();
    const until = body?.valid_until ? Date.parse(body.valid_until) : NaN;
    return Number.isFinite(until) && until <= Date.now();
  } catch {
    return false;
  }
}

async function confirmExpired() {
  if (expired) { return; }
  if (!checking) {
    lastProbeAt = Date.now();
    // The page's own fetch, at call time: /bff/session is never watched, so this cannot loop.
    checking = window.fetch('/bff/session', { credentials: 'include', headers: { Accept: 'application/json' } })
      .then(sessionGone)
      .catch(() => false) // offline or the bff restarting: not an expiry
      .finally(() => { checking = null; });
  }
  if (await checking) { announce(); }
}

/** `fetchImpl`, plus: a 401 from Flow's own APIs checks the session once and announces an expiry. */
export function watchSessionExpiry(fetchImpl) {
  return async(input, init) => {
    // Expired: no more calls to Flow's APIs (polling would only collect 401s) until the sign-in reloads the page.
    if (expired && watchesPath(input)) {
      return new Response('Your Flow session expired', { status: 401, headers: { 'Content-Type': 'text/plain' } });
    }
    const response = await fetchImpl(input, init);
    if (response?.status === 401 && watchesPath(input)) { confirmExpired(); }
    return response;
  };
}

/**
 * When the tab comes back (focus, visible) more than `minMs` after the last check, asks /bff/session once.
 * Returns the unsubscribe.
 */
export function watchSessionOnFocus(minMs = FOCUS_PROBE_MS) {
  const onBack = () => {
    if (expired || document.visibilityState === 'hidden') { return; }
    if (Date.now() - lastProbeAt < minMs) { return; }
    confirmExpired();
  };
  window.addEventListener('focus', onBack);
  document.addEventListener('visibilitychange', onBack);
  return () => {
    window.removeEventListener('focus', onBack);
    document.removeEventListener('visibilitychange', onBack);
  };
}

/**
 * Flow's preview frame saying the gateway refused the session (the "Sign in to see this preview" page): from Flow's
 * own origin only, it announces the expiry, so the whole app asks to sign in again. Returns the unsubscribe.
 */
export function watchPreviewFrames(origin = window.location.origin) {
  const onMessage = (event) => {
    if (event.origin !== origin) { return; }
    if (event.data?.type === PREVIEW_UNAUTHORIZED) { announce(); return; }
    if (event.data?.type === PREVIEW_LOGIN_LOOP) {
      const { project, service } = event.data;
      if (typeof project !== 'string' || !NAME.test(project)) { return; }
      const detail = { project, service: typeof service === 'string' && NAME.test(service) ? service : null };
      window.dispatchEvent(new CustomEvent(LOGIN_LOOP_EVENT, { detail }));
    }
  };
  window.addEventListener('message', onMessage);
  return () => window.removeEventListener('message', onMessage);
}

/** Calls `listener({project, service})` each time a preview frame reports the app's sign-in loop; returns the unsubscribe. */
export function onPreviewLoginLoop(listener) {
  const handle = (event) => listener(event.detail);
  window.addEventListener(LOGIN_LOOP_EVENT, handle);
  return () => window.removeEventListener(LOGIN_LOOP_EVENT, handle);
}

/** Where the person fixes it: the project's code (`?tab=code`: Code at Home, the project files), in its own organization (pages/OpenProject.jsx). */
export function loginLoopCodeUrl({ project }) {
  return `/open/${encodeURIComponent(project)}?tab=code`;
}

/** Tests only: forget a previous expiry. */
export function resetSessionExpiry() { expired = false; checking = null; lastProbeAt = Date.now(); }
